import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";

export interface Patrimonio {
  id: string;
  user_id: string;
  item: string;
  codigo: string;
  categoria: string;
  valor: number;
  valor_atual?: number;
  data_aquisicao: string;
  ultima_manutencao?: string;
  proxima_manutencao?: string;
  responsavel_manutencao?: string;
  status: string;
  observacoes?: string;
  created_at: string;
  updated_at: string;
}

export interface PatrimonioInput {
  item: string;
  codigo: string;
  categoria: string;
  valor: number;
  valor_atual?: number;
  data_aquisicao: string;
  ultima_manutencao?: string;
  proxima_manutencao?: string;
  responsavel_manutencao?: string;
  status: string;
  observacoes?: string;
}

export function usePatrimonio() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: patrimonios = [], isLoading } = useQuery({
    queryKey: ["patrimonio", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const data = await apiList<Patrimonio>("patrimonio");
      return data.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    },
    enabled: !!user?.id,
  });

  const createPatrimonio = useMutation({
    mutationFn: async (input: PatrimonioInput) => {
      if (!user?.id) throw new Error("User not authenticated");

      return apiCreate<Patrimonio>("patrimonio", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patrimonio"] });
      toast({
        title: "Patrimônio cadastrado",
        description: "O item foi cadastrado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao cadastrar",
        description: "Não foi possível cadastrar o item.",
        variant: "destructive",
      });
    },
  });

  const updatePatrimonio = useMutation({
    mutationFn: async ({ id, ...input }: PatrimonioInput & { id: string }) => {
      return apiUpdate<Patrimonio>("patrimonio", id, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patrimonio"] });
      toast({
        title: "Patrimônio atualizado",
        description: "O item foi atualizado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o item.",
        variant: "destructive",
      });
    },
  });

  const deletePatrimonio = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("patrimonio", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patrimonio"] });
      toast({
        title: "Patrimônio excluído",
        description: "O item foi excluído com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o item.",
        variant: "destructive",
      });
    },
  });

  return {
    patrimonios,
    isLoading,
    createPatrimonio,
    updatePatrimonio,
    deletePatrimonio,
  };
}
